import { isDefined } from "@clipboard-health/util-ts";
import { usePaginatedWorkerShifts } from "@src/appV2/redesign/WorkerShifts/api/usePaginatedWorkerShifts";
import { parseISO } from "date-fns";

interface UseIsDoubleBookedShiftProps {
  workerId: string;
  shift: {
    id: string;
    attributes: {
      start: string;
      workplaceId: string;
    };
  };
  enabled?: boolean;
}

/**
 * Hook to determine if a shift would result in a double booking for a worker.
 *
 * A double booking occurs when a worker has more than one shift that overlaps
 * with the time period of the given shift.
 *
 * @param props - Configuration object
 * @param props.workerId - The ID of the worker to check for double bookings
 * @param props.shift - The shift to check for double booking conflicts
 * @param props.enabled - Whether the hook should be enabled (defaults to true)
 *
 * @returns Object containing:
 * - isDoubleBookedShift: boolean indicating if the shift would be a double booking
 * - isLoading: boolean indicating if the check is in progress
 * - fetchStatus: the fetch status from the underlying query
 */
export function useIsDoubleBookedShift(props: UseIsDoubleBookedShiftProps) {
  const { workerId, shift, enabled = true } = props;

  const {
    data: workerShiftsData,
    isLoading,
    fetchStatus,
  } = usePaginatedWorkerShifts(
    {
      workerId,
      workplaceId: shift.attributes.workplaceId ?? "",
      filter: isDefined(shift)
        ? {
            start: {
              lt: parseISO(shift.attributes.start),
            },
            end: {
              gte: parseISO(shift.attributes.start),
            },
            deleted: false,
          }
        : undefined,
    },
    {
      enabled,
    }
  );

  return {
    isDoubleBookedShift: (workerShiftsData?.pages.flatMap((page) => page.data) ?? []).length > 0,
    isLoading: isLoading && fetchStatus !== "idle",
  };
}
